import React, { useState, useEffect } from "react";
import { db, ref, onValue, get } from "./firebase";
import { auth } from "./firebase";
import { FiCalendar, FiVideo, FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { DashboardLoading, DashboardNotice, EmptyState, NoCourse } from "./DashboardStates";

const StudentMeetings = () => {
  const [allMeetings, setAllMeetings] = useState([]);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (foundUser && userCourse) {
          setEnrolledCourse(userCourse);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!enrolledCourse) return;

    const meetingsRef = ref(db, `courses/${enrolledCourse}/meetings`);
    const unsubscribeMeetings = onValue(meetingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const meetingsData = Object.entries(snapshot.val())
          .map(([id, data]) => ({
            id,
            ...data,
            meetingTime: data.meetingTime || "",
            published: data.published !== false // Default to true
          }))
          .filter(meeting => meeting.published) // Only show published meetings
          .sort((a, b) => {
            // Sort by meeting time
            const timeA = new Date(a.meetingTime);
            const timeB = new Date(b.meetingTime);
            return timeA - timeB;
          });

        setAllMeetings(meetingsData);
      } else {
        setAllMeetings([]);
      }
      setLoading(false);
    });

    return () => unsubscribeMeetings();
  }, [enrolledCourse]);

  // Helper function to check if meeting has ended (2 hours after meeting time)
  const isMeetingEnded = (meetingTime) => {
    if (!meetingTime) return true;

    const now = new Date();
    const meetingDate = new Date(meetingTime);
    const twoHoursAfter = new Date(meetingDate.getTime() + 2 * 60 * 60 * 1000);

    return now > twoHoursAfter;
  };

  // Filter meetings based on search query
  const filteredMeetings = React.useMemo(() => {
    return allMeetings.filter(meeting => {
      // Filter by search query
      const matchesSearch = searchQuery === '' ||
        meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (meeting.description && meeting.description.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesSearch;
    }).sort((a, b) => {
      // Sort meetings by time
      return new Date(a.meetingTime) - new Date(b.meetingTime);
    });
  }, [allMeetings, searchQuery]);

  const toggleMeetingDetails = (id) => {
    setExpandedMeetingId(expandedMeetingId === id ? null : id);
  };

  // Helper function to show meeting status
  const getMeetingStatus = (meetingTime) => {
    if (!meetingTime) return "No time specified";

    if (isMeetingEnded(meetingTime)) return "Meeting ended";

    const now = new Date();
    const meetingDate = new Date(meetingTime);
    const diff = meetingDate - now;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    let result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0 && days === 0) result.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);

    return result.join(" and ") || "less than a minute";
  };

  if (loading) return <DashboardLoading />;
  if (error) return <DashboardNotice message={error} />;
  if (!enrolledCourse) return <NoCourse description="Once you're enrolled, online meetings will show up here." />;

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Online Meetings</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-blue-100 text-sm md:text-base">Total Meetings:</span>
            <span className="text-white font-bold text-lg md:text-xl">{allMeetings.length}</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search meetings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="dash-input w-full pl-10 p-3 dash-card bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-400"
          />
        </div>
      </div>

      {/* Meetings List */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {filteredMeetings.length > 0 ? (
          filteredMeetings.map((meeting) => (
            <div
              key={meeting.id}
              className="dash-card bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-blue-500/30 transition-all duration-300"
            >
              <div
                className="p-6 cursor-pointer"
                onClick={() => toggleMeetingDetails(meeting.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                      <FiCalendar className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{meeting.title}</h3>
                      <div className="flex items-center space-x-3 text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <FiCalendar className="text-gray-400" />
                          <span>{new Date(meeting.meetingTime).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">•</span>
                          <span>{getMeetingStatus(meeting.meetingTime)}</span>
                        </div>
                      </div>
                      {meeting.description && (
                        <p className="mt-3 text-sm text-gray-400">{meeting.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-gray-400">
                    {expandedMeetingId === meeting.id ? <FiChevronUp className="text-xl" /> : <FiChevronDown className="text-xl" />}
                  </div>
                </div>
              </div>

              {expandedMeetingId === meeting.id && meeting.link && (
                <div className="px-6 pb-6">
                  <a
                    href={meeting.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-white ${isMeetingEnded(meeting.meetingTime)
                      ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      } transition-colors`}
                  >
                    <FiVideo className="text-xl" />
                    <span>
                      {isMeetingEnded(meeting.meetingTime)
                        ? 'View Details'
                        : 'Join Meeting'}
                    </span>
                  </a>
                </div>
              )}
            </div>
          ))
        ) : (
          <EmptyState
            icon={FiCalendar}
            title={searchQuery ? "No matching meetings" : "No meetings yet"}
            description={searchQuery ? "Try a different search." : "Live sessions will appear here once they're scheduled."}
          />
        )}
      </div>
    </div>
  );
};

export default StudentMeetings;